import { Injectable } from '@angular/core';
import { Params } from '@angular/router';
import { FluidHostContext } from '../../fluid-content/scopes/fluid-host-context';
import { FluidInstrumentationService } from '../../fluid-content/utils/fluid-instrumentation-service';
import { RoutePaths } from '../../shared/constants/route-paths.constant';
import { WindowService } from '../../shared/services/window.service';
import { UrlBuilder } from '../../shared/utilities/url-builder.utility';
import { LinkTarget } from '../constants/link-target.enum';
import { OfficeRoute } from '../constants/office-route.enum';
import { KnownReferralPlatform } from '../instrumentation/instrumentation.service';
import { FileType } from '../models/file-item.model';
import { DocumentInfo } from '../models/wac-host.model';
import { Workload } from '../models/workload.model';
import { AccessTokenService } from '../services/access-token.service';
import { WacContentService } from '../services/wac-content.service';
import { CapabilitiesService } from '../shared-context/capabilities.service';
import { ContextService } from '../shared-context/context.service';
import { LocalAppLauncherService } from './local-app-launcher.service';
import { OfficeRouteItem, OfficeRouteService } from './office-route.service';
import { LaunchAction, UrlLauncherService } from './url-launcher.service';

export const enum AppLaunchResult {
    Failure = 'Failure',
    Native = 'Native',
    StartPage = 'StartPage',
    Web = 'Web'
}
export enum AppContentType {
    WAC = 'wac',
    Fluid = 'fluid',
    MOS = 'mos'
}
interface BaseAppDetails<T extends AppContentType> {
    type: T;
    event?: Event;
    fileName?: string;
    launchUrl?: string;
    launchAction: LaunchAction;
}
export interface WACAppDetails extends BaseAppDetails<AppContentType.WAC> {
    id?: string;
    fileType: FileType;
    isLocal?: boolean;
    isOnPremOrVanity?: boolean;
    driveId?: string;
    docId?: string;
    docUrl: string;
    fileName?: string;
    isConsumer?: boolean;
    wdOrigin?: string;
    siteUrl?: string;
}
export interface FluidAppDetails extends BaseAppDetails<AppContentType.Fluid> {
    fileType: FileType;
    siteUrl?: string;
    driveId?: string;
    docId?: string;
    docUrl: string;
}
export interface MOSAppDetails extends BaseAppDetails<AppContentType.MOS> {
    application: string;
    extension: string;
    id: string;
    fileType?: FileType;
}
export type LaunchAppDetails = WACAppDetails | FluidAppDetails | MOSAppDetails;
export type PostLaunchCallback = (success: boolean, platform?: KnownReferralPlatform, nav?: boolean, uiHost?: boolean) => void;
@Injectable({
    providedIn: 'root'
})
export class AppLauncherService {

    constructor(
        private readonly localAppLauncherService: LocalAppLauncherService,
        private readonly urlLauncherService: UrlLauncherService,
        private readonly capabilitiesService: CapabilitiesService,
        private readonly contextService: ContextService,
        private readonly accessTokenService: AccessTokenService,
        private readonly officeRouteService: OfficeRouteService,
        private readonly fluidInstrumentationService: FluidInstrumentationService,
        private readonly wacContentService: WacContentService,
        private readonly fluidHostContext: FluidHostContext,
        private readonly windowService: WindowService

    ) { }
    /**
     *Launches the app on the appropriate platform.
     * If this is in the context of the PWA app:
     *     - If a native app exists, it is launched.
     *     - If a native app doesn't exist or there was an error launching it:
     *         - If the app supports in-app navigation, then navigates to the corresponding route
     *         - If the app doesn't support in-app navigation, then it opens the app in the browser.
     * If this is in the context of www.office.com:
     *     - If the app supports in-app navigation, then navigates to the corresponding route
     *     - If the app doesn't support in-app navigation, then it opens the app in a new tab (does not call preventDefault).
     * If this is in the context of Edge NTP, the app is opened in the same tab (does not call preventDefault).
     */
    public launchApp(workload: Workload, event: Event, action: LaunchAction, defaultLinkBehaviorDisabled: boolean = false): Promise<AppLaunchResult> {
        // Launch native Office app if available on the machine
        const prelaunchCallback = (event && !event.defaultPrevented && !defaultLinkBehaviorDisabled) ? () => event.preventDefault() : null;
        return this.localAppLauncherService.launchOfficeApp(workload.Text, '', prelaunchCallback).then(success => {
            if (!success) {
                return this.launchOfficeWebApp(workload, event, true, action);
            }

            return AppLaunchResult.Native;
        }).catch(() => this.launchOfficeWebApp(workload, event, defaultLinkBehaviorDisabled, action));
    }
    public launchContentOrAppUrl(launchUrl: string, launchAction: LaunchAction, linkTarget?: LinkTarget, postLaunchCallback?: PostLaunchCallback): void {
        this.urlLauncherService.openUrl(launchUrl, launchAction, linkTarget).then(() => {
            postLaunchCallback?.(true);
        }, () => {
            postLaunchCallback?.(false);
        });
    }
    /* Single place to manage and open the content of all type of files.
       Opens content for all types of files like Thirdy party, fluid,wacHost.
       check the right FileTpe for each request and opens the contents */
    public launchDocument(appDetails: LaunchAppDetails, postLaunchCallback?: PostLaunchCallback): void {
        const { event } = appDetails;
        if (event && event['ctrlKey']) {
            this.launchUrlWithAppDetails(appDetails, postLaunchCallback);
            return;
        }
        switch (appDetails.type) {
            /*checks if it a wac file and routes accordingly
            checks if call is made from shared-search UX */
            case AppContentType.WAC:
                {
                    const { isConsumer, isOnPremOrVanity } = appDetails;
                    const hasTokenForWacHost = ((!isOnPremOrVanity || isConsumer) && this.accessTokenService.hasOneDriveToken())
                        || this.contextService.sharedContext.inMockMode;
                    if (hasTokenForWacHost) {
                        this.openWacContent(appDetails);
                    } else {
                        postLaunchCallback?.(false);
                        this.launchContentOrAppUrl(appDetails.launchUrl, LaunchAction.OpenMruItem, (appDetails.fileType === FileType.Forms || appDetails.fileType === FileType.FormsQuiz) ? LinkTarget.SameFrame : null, postLaunchCallback);
                    }
                }
                break;
            /*checks if it a fluid file and routes accordingly
            checks if call is made from shared-search UX */
            case AppContentType.Fluid:
                this.openFluidContent(appDetails, true);
                break;
            /*check if it is a third party app*/
            case AppContentType.MOS:
                {
                    const { application, extension, id } = appDetails;
                    this.navigateToApp(application, extension, id);
                }
                break;
        }
    }
    private launchOfficeWebApp(workload: Workload, event: Event, defaultLinkBehaviorDisabled: boolean, action: LaunchAction): Promise<AppLaunchResult> {
        if (workload.InAppNav && workload.FileType) {
            if (event && !event.defaultPrevented && !defaultLinkBehaviorDisabled) {
                event.preventDefault();
            }
            this.officeRouteService.navigateTo({ route: OfficeRoute.Start, app: workload.FileType });
            return Promise.resolve(AppLaunchResult.StartPage);
        } else if (defaultLinkBehaviorDisabled || !event || event.defaultPrevented) {
            return this.urlLauncherService.openUrl(workload.Url, action).then(() => AppLaunchResult.Web, () => AppLaunchResult.Failure);
        }
        return Promise.resolve(AppLaunchResult.Web);
    }
    private navigateToApp(application: string, extension: string, id: string): void {
        this.officeRouteService.navigateToPath(
            this.capabilitiesService.capabilities.m365AppsPageRootPath,
            application,
            // TODO: replace extension with the field that is used for 'entityId'
            extension,
            id
        );
    }
    private setFluidHostContext(startTime: number) {
        this.fluidInstrumentationService.logPerformanceBootStart(this.fluidHostContext.getBootId());
        this.fluidHostContext.hostScenarioName = 'DocumentClick';
        this.fluidHostContext.hostScenarioStartTime = startTime;
    }
    private openFluidContent(appDetails: FluidAppDetails, fluidHostFile: boolean) {
        const startTime = Date.now();
        this.setFluidHostContext(startTime);
        const { driveId, docUrl, docId } = appDetails;
        const route: OfficeRouteItem = { route: OfficeRoute.Content, app: appDetails.fileType };
        const params: Params = WacContentService.getWacParams(
            driveId,
            docId,
            docUrl,
            fluidHostFile ? { siteUrl: appDetails.siteUrl } : {}
        );
        this.officeRouteService.navigateTo(
            route,
            false,
            params
        );
    }
    private generateDocumentInfo(appDetails: WACAppDetails): DocumentInfo {
        return {
            docUrl: appDetails.docUrl,
            fileName: appDetails.fileName,
            fileType: appDetails.fileType,
            wdOrigin: appDetails.wdOrigin,
            docId: appDetails.docId,
            driveId: appDetails.driveId
        };
    }
    private openWacContent(appDetails: WACAppDetails): void {
        const documentInfo: DocumentInfo = this.generateDocumentInfo(appDetails);
        const startTime = Date.now();
        this.wacContentService.bootstrapAndRoute(
            documentInfo,
            undefined,
            startTime
        );
    }
    /* form the url when ctrlKey is pressed and send the url to open in new tab, created as separate method for re-usability .
           open in browser */
    private launchUrlWithAppDetails(appDetails: LaunchAppDetails, postLaunchCallback?: PostLaunchCallback): void {
        const newPath = `/${RoutePaths.ContentHost.replace(':app', FileType[appDetails.fileType])}`;
        const linkTarget = (FileType.Forms === appDetails.fileType || FileType.FormsQuiz === appDetails.fileType) ? LinkTarget.SameFrame : LinkTarget.NewTab;
        let launchUrl: string;
        //TODO: meeting artifact fluid item
        let params: Params;
        if (appDetails.type === AppContentType.Fluid || appDetails.type === AppContentType.WAC) {
            const { driveId, docUrl, docId } = appDetails;
            params = WacContentService.getWacParams(
                driveId,
                docId,
                docUrl,
                appDetails.siteUrl ? { siteUrl: appDetails.siteUrl } : {}
            );
            launchUrl = UrlBuilder.replacePathAndAddParams(
                this.windowService.nativeWindow.location.href,
                newPath,
                params
            );
        } else if (appDetails.type === AppContentType.MOS) {
            const { application, extension, id } = appDetails;
            launchUrl = this.officeRouteService.getNavigationUrl(
                this.capabilitiesService.capabilities.m365AppsPageRootPath,
                application,
                // TODO: replace extension with the field that is used for 'entityId'
                extension,
                id
            );
        }
        this.launchContentOrAppUrl(launchUrl, appDetails.launchAction, linkTarget, postLaunchCallback);
    }
}
